import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TimePipe } from "../../Pipes/time.pipe";
import { AudioService } from '../../services/audio.service';
import { MusicService } from '../../services/music.service';
import { ModalService } from '../../services/modal.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { Track, TrackCategory, TrackMood, TrackTempo, TrackKey, TrackInstrument } from '../../models/track.model';
import { Subscription, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-music-page',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        TimePipe
    ],
    templateUrl: './music-page.component.html',
    styleUrl: './music-page.component.scss'
})
export class MusicPageComponent implements OnInit, OnDestroy {
  // Audio player state
  currentTrack: Track | null = null;
  isPlaying = false;
  currentTime = 0;
  duration = 0;
  progress = 0;
  volume = 100;
  repeat: 'none' | 'all' | 'one' = 'none';
  shuffle = false;
  isDragging = false;
  audio = new Audio();
  
  // Filters
  searchQuery = '';
  selectedCategory = 'all';
  selectedMood = 'all';
  selectedTempo = 'all';
  selectedKey = 'all';
  selectedInstrument = 'all';
  
  // View options
  viewMode: 'grid' | 'list' = 'grid';
  sortBy: 'relevance' | 'title' | 'artist' | 'duration' | 'date' = 'relevance';
  sortDirection: 'asc' | 'desc' = 'asc';
  
  // Data
  categories: TrackCategory[] = [];
  moods: TrackMood[] = [];
  tempos: TrackTempo[] = [];
  keys: TrackKey[] = [];
  instruments: TrackInstrument[] = [];
  allTracks: Track[] = [];
  filteredTracks: Track[] = [];
  featuredTracks: Track[] = [];
  
  // UI state
  isFilterPanelOpen = false;
  isAdvancedFiltersVisible = false;
  isLoading = true;
  activeFilters: string[] = [];
  
  // Pagination
  currentPage = 1;
  pageSize = 12;
  totalPages = 1;
  
  // Selected track for details view
  selectedTrackForDetails: Track | null = null;
  similarTracks: Track[] = [];
  
  // Component ID for audio service
  private componentId: string;
  private subscriptions: Subscription = new Subscription();
  
  constructor(
    private audioService: AudioService,
    private musicService: MusicService,
    private modalService: ModalService,
    private tokenStorage: TokenStorageService
  ) {
    this.componentId = 'music_player_' + Math.random().toString(36).substr(2, 9);
  }
  
  ngOnInit() {
    this.setupAudioEvents();
    this.loadData();
    
    // Précharger les composants nécessaires
    this.preloadComponents();
  }
  
  ngOnDestroy() {
    this.audio.pause();
    this.audio.src = '';
    this.audio.removeEventListener('timeupdate', () => {});
    this.audio.removeEventListener('loadedmetadata', () => {});
    this.audio.removeEventListener('ended', () => {});
    this.audio.removeEventListener('error', () => {});
    this.subscriptions.unsubscribe();
  }
  
  // Préchargement des composants pour éviter les problèmes d'importation
  private preloadComponents() {
    // Précharger le composant de demande de téléchargement
    import('../../modal/download-request-form/download-request-form.component');
    // Précharger le composant de connexion
    import('../login-space/login-space.component');
  }
  
  // Load all necessary data
  private loadData(): void {
    this.isLoading = true;
    
    // Subscribe to categories
    this.subscriptions.add(
      this.musicService.categories$.subscribe(categories => {
        this.categories = categories;
      })
    );
    
    // Subscribe to moods
    this.subscriptions.add(
      this.musicService.moods$.subscribe(moods => {
        this.moods = moods;
      })
    );
    
    // Subscribe to tempos
    this.subscriptions.add(
      this.musicService.tempos$.subscribe(tempos => {
        this.tempos = tempos;
      })
    );
    
    // Subscribe to keys
    this.subscriptions.add(
      this.musicService.keys$.subscribe(keys => {
        this.keys = keys;
      })
    );
    
    // Subscribe to instruments
    this.subscriptions.add(
      this.musicService.instruments$.subscribe(instruments => {
        this.instruments = instruments;
      })
    );
    
    // Subscribe to tracks
    this.subscriptions.add(
      this.musicService.tracks$.subscribe(tracks => {
        this.allTracks = tracks;
        this.applyFilters();
        this.isLoading = false;
      })
    );
    
    // Subscribe to featured tracks
    this.subscriptions.add(
      this.musicService.getFeaturedTracks().subscribe(tracks => {
        this.featuredTracks = tracks;
      })
    );
  }
  
  // Apply filters to tracks
  applyFilters(): void {
    this.updateActiveFilters();
    
    this.subscriptions.add(
      this.musicService.filterTracks(
        this.searchQuery,
        this.selectedCategory,
        this.selectedMood,
        this.selectedTempo,
        this.selectedKey,
        this.selectedInstrument
      ).subscribe(tracks => {
        // Apply sorting
        const sortedTracks = this.sortTracks(tracks);
        
        // Update filtered tracks
        this.filteredTracks = sortedTracks;
        
        // Update pagination
        this.totalPages = Math.ceil(this.filteredTracks.length / this.pageSize);
        if (this.currentPage > this.totalPages) {
          this.currentPage = 1;
        }
      })
    );
  }
  
  // Sort tracks based on current sort settings
  sortTracks(tracks: Track[]): Track[] {
    return [...tracks].sort((a, b) => {
      let comparison = 0;
      
      switch (this.sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'artist':
          comparison = a.artist.localeCompare(b.artist);
          break;
        case 'duration':
          // Convert duration strings to seconds for comparison
          const aDuration = this.durationToSeconds(a.duration);
          const bDuration = this.durationToSeconds(b.duration);
          comparison = aDuration - bDuration;
          break;
        case 'date':
          if (a.releaseDate && b.releaseDate) {
            comparison = new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
          }
          break;
        case 'relevance':
        default:
          // For relevance, featured tracks come first
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return 0;
      }
      
      // Apply sort direction
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }
  
  // Convert duration string (MM:SS) to seconds
  durationToSeconds(duration: string): number {
    const parts = duration.split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  
  // Update active filters array for UI display
  updateActiveFilters(): void {
    this.activeFilters = [];
    
    if (this.selectedCategory !== 'all') {
      const category = this.categories.find(c => c.id === this.selectedCategory);
      if (category) {
        this.activeFilters.push(`Category: ${category.name}`);
      }
    }
    
    if (this.selectedMood !== 'all') {
      const mood = this.moods.find(m => m.id === this.selectedMood);
      if (mood) {
        this.activeFilters.push(`Mood: ${mood.name}`);
      }
    }
    
    if (this.selectedTempo !== 'all') {
      const tempo = this.tempos.find(t => t.id === this.selectedTempo);
      if (tempo) {
        this.activeFilters.push(`Tempo: ${tempo.name}`);
      }
    }
    
    if (this.selectedKey !== 'all') {
      const key = this.keys.find(k => k.id === this.selectedKey);
      if (key) {
        this.activeFilters.push(`Key: ${key.name}`);
      }
    }
    
    if (this.selectedInstrument !== 'all') {
      const instrument = this.instruments.find(i => i.id === this.selectedInstrument);
      if (instrument) {
        this.activeFilters.push(`Instrument: ${instrument.name}`);
      }
    }
  }
  
  // Reset all filters
  resetFilters(): void {
    this.searchQuery = '';
    this.selectedCategory = 'all';
    this.selectedMood = 'all';
    this.selectedTempo = 'all';
    this.selectedKey = 'all';
    this.selectedInstrument = 'all';
    this.applyFilters();
  }
  
  // Remove a specific filter
  removeFilter(filter: string): void {
    const [type, value] = filter.split(': ');
    
    switch (type) {
      case 'Category':
        this.selectedCategory = 'all';
        break;
      case 'Mood':
        this.selectedMood = 'all';
        break;
      case 'Tempo':
        this.selectedTempo = 'all';
        break;
      case 'Key':
        this.selectedKey = 'all';
        break;
      case 'Instrument':
        this.selectedInstrument = 'all';
        break;
    }
    
    this.applyFilters();
  }
  
  // Toggle filter panel visibility
  toggleFilterPanel(): void {
    this.isFilterPanelOpen = !this.isFilterPanelOpen;
  }
  
  // Toggle advanced filters visibility
  toggleAdvancedFilters(): void {
    this.isAdvancedFiltersVisible = !this.isAdvancedFiltersVisible;
  }
  
  // Change view mode (grid or list)
  changeViewMode(mode: 'grid' | 'list'): void {
    this.viewMode = mode;
  }
  
  // Change sort criteria
  changeSortBy(sortBy: 'relevance' | 'title' | 'artist' | 'duration' | 'date'): void {
    if (this.sortBy === sortBy) {
      // Toggle direction if clicking the same sort option
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = sortBy;
      // Default to ascending for title/artist, descending for date/relevance
      this.sortDirection = (sortBy === 'title' || sortBy === 'artist') ? 'asc' : 'desc';
    }
    
    this.applyFilters();
  }
  
  // Get current page of tracks
  getCurrentPageTracks(): Track[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.filteredTracks.slice(startIndex, endIndex);
  }
  
  // Go to a specific page
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }
  
  // Get array of page numbers for pagination
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    
    if (this.totalPages <= maxPagesToShow) {
      // Show all pages if there are few
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show a subset of pages with current page in the middle if possible
      let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = startPage + maxPagesToShow - 1;
      
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  }
  
  // Show track details
  showTrackDetails(track: Track): void {
    this.selectedTrackForDetails = track;
    
    // Load similar tracks
    this.subscriptions.add(
      this.musicService.getSimilarTracks(track.id).subscribe(tracks => {
        this.similarTracks = tracks;
      })
    );
  }
  
  // Close track details
  closeTrackDetails(): void {
    this.selectedTrackForDetails = null;
  }
  
  // Audio player methods
  private setupAudioEvents() {
    this.audio.addEventListener('timeupdate', () => {
      if (!this.isDragging) {
        this.currentTime = this.audio.currentTime;
        this.progress = (this.audio.currentTime / this.audio.duration) * 100;
      }
    });

    this.audio.addEventListener('loadedmetadata', () => {
      this.duration = this.audio.duration;
    });

    this.audio.addEventListener('ended', () => {
      this.handleTrackEnd();
    });

    this.audio.addEventListener('error', (e) => {
      console.error('Audio error:', e);
      console.error('Error code:', this.audio.error ? this.audio.error.code : 'unknown');
      console.error('Audio URL:', this.audio.src);
      this.isPlaying = false;
    });
  }
  
  playTrack(track: Track) {
    if (this.currentTrack?.id === track.id) {
      this.togglePlay();
      return;
    }

    if (this.currentTrack) {
      this.audio.pause();
    }

    this.currentTrack = track;
    
    try {
      if (!track.url) {
        console.error('Invalid audio URL:', track.url);
        this.isPlaying = false;
        return;
      }
      
      this.audio.src = track.url;
      this.audio.load();
      this.audio.play().then(() => {
        this.isPlaying = true;
        this.audioService.setActivePlayer(this.componentId);
      }).catch(error => {
        console.error('Error playing audio:', error);
        this.isPlaying = false;
      });
    } catch (e) {
      console.error('Exception during playback:', e);
      this.isPlaying = false;
    }
  }
  
  togglePlay() {
    if (!this.currentTrack && this.filteredTracks.length > 0) {
      this.playTrack(this.filteredTracks[0]);
      return;
    }

    if (this.isPlaying) {
      this.audio.pause();
    } else {
      this.audio.play();
      this.audioService.setActivePlayer(this.componentId);
    }
    this.isPlaying = !this.isPlaying;
  }
  
  private handleTrackEnd() {
    if (!this.currentTrack) return;
    
    const currentIndex = this.filteredTracks.findIndex(t => t.id === this.currentTrack!.id);
    
    switch (this.repeat) {
      case 'none':
        if (currentIndex < this.filteredTracks.length - 1) {
          this.playTrack(this.filteredTracks[currentIndex + 1]);
        } else {
          this.isPlaying = false;
          this.currentTime = 0;
          this.progress = 0;
        }
        break;

      case 'all':
        if (currentIndex < this.filteredTracks.length - 1) {
          this.playTrack(this.filteredTracks[currentIndex + 1]);
        } else {
          this.playTrack(this.filteredTracks[0]);
        }
        break;

      case 'one':
        this.audio.currentTime = 0;
        this.audio.play();
        break;
    }
  }
  
  previousTrack() {
    if (!this.currentTrack && this.filteredTracks.length > 0) {
      this.playTrack(this.filteredTracks[0]);
      return;
    }

    if (!this.currentTrack) return;
    
    const currentIndex = this.filteredTracks.findIndex(t => t.id === this.currentTrack!.id);
    
    if (currentIndex > 0) {
      this.playTrack(this.filteredTracks[currentIndex - 1]);
    } else if (this.repeat === 'all') {
      this.playTrack(this.filteredTracks[this.filteredTracks.length - 1]);
    }
  }
  
  nextTrack() {
    if (!this.currentTrack && this.filteredTracks.length > 0) {
      this.playTrack(this.filteredTracks[0]);
      return;
    }

    if (!this.currentTrack) return;
    
    const currentIndex = this.filteredTracks.findIndex(t => t.id === this.currentTrack!.id);
    
    if (currentIndex < this.filteredTracks.length - 1) {
      this.playTrack(this.filteredTracks[currentIndex + 1]);
    } else if (this.repeat === 'all') {
      this.playTrack(this.filteredTracks[0]);
    }
  }
  
  toggleRepeat() {
    switch (this.repeat) {
      case 'none':
        this.repeat = 'all';
        break;
      case 'all':
        this.repeat = 'one';
        break;
      case 'one':
        this.repeat = 'none';
        break;
    }
  }
  
  toggleShuffle() {
    this.shuffle = !this.shuffle;
    if (this.shuffle) {
      // Shuffle the tracks
      this.filteredTracks = [...this.filteredTracks].sort(() => Math.random() - 0.5);
    } else {
      // Restore original order
      this.applyFilters();
    }
  }
  
  startSeek(event: MouseEvent) {
    this.isDragging = true;
    this.seeking(event);
  }

  seeking(event: MouseEvent) {
    if (!this.isDragging) return;
    
    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    this.progress = percent * 100;
  }

  endSeek() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.audio.currentTime = (this.progress / 100) * this.audio.duration;
  }
  
  setVolume(event: Event) {
    const input = event.target as HTMLInputElement;
    this.volume = parseInt(input.value);
    this.audio.volume = this.volume / 100;
  }
  
  // Helper methods for display
  getCategoryName(categoryId: string): string {
    const category = this.categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  }
  
  getMoodNames(moodIds: string[]): string {
    return moodIds.map(id => {
      const mood = this.moods.find(m => m.id === id);
      return mood ? mood.name : id;
    }).join(', ');
  }
  
  // Download track
  downloadTrack(track: Track): void {
    // Check if user is logged in
    const user = this.tokenStorage.getUser();
    
    if (user && user.token) {
      // User is logged in, proceed with download
      this.initiateDownload(track);
    } else {
      // User is not logged in, show download request form
      this.showDownloadRequestModal(track);
    }
  }
  
  // Show download request modal
  showDownloadRequestModal(track: Track): void {
    import('../../modal/download-request-form/download-request-form.component').then(module => {
      const DownloadRequestFormComponent = module.DownloadRequestFormComponent;
      
      this.modalService.open({
        id: 'download-request-modal',
        title: 'DOWNLOAD.REQUEST_TITLE',
        component: DownloadRequestFormComponent,
        data: { track },
        centered: true,
        size: 'md'
      });
    }).catch(error => {
      console.error('Error loading download request form component:', error);
    });
  }
  
  // Show login modal
  showLoginModal(): void {
    import('../login-space/login-space.component').then(module => {
      const LoginSpaceComponent = module.LoginSpaceComponent;
      
      this.modalService.open({
        id: 'login-modal',
        title: 'LOGIN.WELCOME_BACK',
        component: LoginSpaceComponent,
        centered: true,
        size: 'md'
      });
    }).catch(error => {
      console.error('Error loading login component:', error);
    });
  }
  
  // Initiate actual download
  private initiateDownload(track: Track): void {
    // In a real app, this would create a download link with authentication token
    console.log(`Downloading track: ${track.title}`);
    
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = track.url;
    link.download = `${track.artist} - ${track.title}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  // Get mood color for UI display
  getMoodColor(moodId: string): string {
    const mood = this.moods.find(m => m.id === moodId);
    return mood && mood.color ? mood.color : '#cccccc';
  }
  
  // Track by function for ngFor performance
  trackByFn(index: number, item: Track): number {
    return item.id;
  }
}